import type {
  IAgoraRTCClient,
  IMicrophoneAudioTrack,
  ICameraVideoTrack,
  IAgoraRTCRemoteUser
} from 'agora-rtc-sdk-ng'
let client = undefined as IAgoraRTCClient | undefined
// Vue3 Proxy代理会导致和对象指针不一致，因AgoraSDK的 Api 会检验传的user对象和client里存的user对象是否指针一致，所以不存pinia。但会和remoteUsers一直同步数据
let agoraRemoteUsers = {} as { [prop: string]: IAgoraRTCRemoteUser } // 远程用户、主播

export const useAgoraStore = defineStore({
  id: 'agora',
  state: () => ({
    role: '' as 'host' | 'audience' | '', // 直播角色 host:主播，audience:观众
    audioTrack: undefined as IMicrophoneAudioTrack | undefined, // 本地音频轨道
    videoTrack: undefined as ICameraVideoTrack | undefined, // 本地视频轨道
    remoteUsers: {} as { [prop: string]: IAgoraRTCRemoteUser }, // 远程用户、主播
    joined: false as boolean, // 是否跑完整个流程进房或发布
    authPopupConfig: {
      // 授权弹窗
      isShow: false, // 是否显示
      isLazyShow: false // 是否加载
    },
    isSkipAuth: false, // agora.getDevices 是否跳过权限
    isAutoplayFailed: false, //是否自动播放失败，用户从未点击过页面有可能会播放音频失败
    /** 摄像头设备数量,超过2个摄像头就代表有前置和后置摄像头，有bug再换 */
    videoinputNum: 0,
    /** 摄像头面向模式，摄像头超过两个才给切换 user:前置摄像头,environment:后置摄像头, '':空值代表无*/
    facingMode: 'user' as 'user' | 'environment',
    /** 用户视频是否发布成功 */
    isVideoPublish: false,
    /** 远程用户是否触发首帧 */
    isRemoteUserPirstFrame: false
  }),
  actions: {
    /** 判断是否开始计费 */
    isStartBilling() {
      const roomStore = useRoomStore()
      // 视频通话
      if (
        roomStore.roomType === constant.ROOM_TYPE.VIDEO_CALL &&
        this.isVideoPublish &&
        this.isRemoteUserPirstFrame
      ) {
        const callStore = useCallStore()
        callStore.onStartBilling()
      }
      // 主播开播
      if (
        roomStore.roomType === constant.ROOM_TYPE.VIDEO_LIVE &&
        roomStore.isHostLive &&
        this.isVideoPublish
      ) {
        roomStore.setIsStartTimer(true)
      }
    },
    // 检测是否支持浏览器
    checkBrowser() {
      const i18n = useNuxtApp().$i18n
      return new Promise((resolve, reject) => {
        // 测试浏览器是否支持 result 为 true 代表支持，false 代表不支持；facebook内置浏览器打开忽略此判断
        const AgoraRTC = window.AgoraRTC
        if (
          AgoraRTC.checkSystemRequirements &&
          !AgoraRTC.checkSystemRequirements() &&
          !common.checkVersion().facebook
        ) {
          closeToast()
          showConfirmDialog({
            message: i18n.t('call.googleChromeTips'),
            confirmButtonText: i18n.t('public.Confirm'),
            showCancelButton: false,
            className: 'private-dialog-customize single-btn'
          }).catch((error) => {
            console.log(error)
          })
          reject(false)
        } else {
          resolve(true)
        }
      })
    },
    // 检测权限设备,检查是否有音视频设备权限
    checkTracks() {
      return new Promise((resolve, reject) => {
        this.checkBrowser()
          .then(() => {
            window.AgoraRTC.getDevices(this.isSkipAuth)
              .then((devices) => {
                if (devices && devices.length) {
                  let audioinput = false
                  let videoinput = false
                  let videoinputNum = 0
                  devices.forEach((v) => {
                    if (v.kind === 'audioinput') {
                      audioinput = true
                    }
                    if (v.kind === 'videoinput') {
                      videoinput = true
                      videoinputNum++
                    }
                  })
                  this.videoinputNum = videoinputNum
                  // 麦克风和摄像头设备都有
                  if (audioinput && videoinput) {
                    this.isSkipAuth = true // 成功一次就跳过权限
                    resolve('success')
                  } else {
                    this.setAuthPopupConfig(true)
                    reject('DEVICE_NOT_FOUND')
                  }
                } else {
                  this.setAuthPopupConfig(true)
                  reject('DEVICE_NOT_FOUND')
                }
              })
              .catch((error) => {
                this.setAuthPopupConfig(true)
                reject(JSON.stringify(error))
              })
          })
          .catch((error) => reject(error))
      })
    },
    // 创建实例
    async create() {
      console.log('AgoraSDK => 执行创建实例函数')
      const audioStore = useAudioStore()
      const i18n = useNuxtApp().$i18n

      // 视频的房间才显示遮罩层
      this.setIsShowMask(true)

      // 因观看直播操作比较快，所以先判断一下是否需要离开函数
      if (client?.channelName) {
        console.log('AgoraSDK => 频道离开 leave')
        await client?.leave()
      }

      // 判断是否需要重新创建实例或新建实例
      if (!client) {
        console.log('AgoraSDK => 重新创建实例')
        const AgoraRTC = window.AgoraRTC
        // 监听自动播放失败
        AgoraRTC.onAutoplayFailed = () => {
          // 引导用户点击页面，可以弹窗提示。
          showConfirmDialog({
            message: i18n.t('room.AtuoPlayFailedMsg'),
            confirmButtonText: i18n.t('public.Confirm'),
            showCancelButton: false,
            className: 'private-dialog-customize single-btn'
          }).catch((error) => {
            console.log(error)
          })
          this.isAutoplayFailed = true
          audioStore.setIsOpenSound(false)
        }
        AgoraRTC.enableLogUpload() // 日志上传
        // this.agora.setLogLevel(0) // 日志输出级别 0:DEBUG 1:INFO 2:WARNING 3:ERROR 4:NONE
        client = AgoraRTC.createClient({
          mode: 'live',
          codec: 'vp8'
        })
      } else {
        console.log('AgoraSDK => 已有实例')
      }
    },
    // 进入频道
    async join() {
      const roomStore = useRoomStore()
      const audioStore = useAudioStore()

      // 视频房、通话房才开启定时器
      if (
        roomStore.roomType === constant.ROOM_TYPE.VIDEO_LIVE ||
        roomStore.roomType === constant.ROOM_TYPE.VIDEO_CALL
      ) {
        roomStore.setIsTimeout() //开启定时器
      }

      if (!roomStore.rtcInfo.token) return
      console.log('AgoraSDK => 开始join')
      try {
        // 设置角色
        // 视频通话房；视频直播房-主播；语音直播房-在麦；
        if (
          roomStore.roomType === constant.ROOM_TYPE.VIDEO_CALL ||
          (roomStore.roomType === constant.ROOM_TYPE.VIDEO_LIVE &&
            roomStore.isHostLive) ||
          (roomStore.roomType === constant.ROOM_TYPE.AUDIO_LIVE &&
            audioStore.isOnMicSeat)
        ) {
          await this.setRole('host')
        } else {
          await this.setRole('audience')
        }

        // 将事件侦听器添加到client
        client?.on('user-published', this.onUserPublished) // 当远端用户成功发布音视频轨道之后
        client?.on('user-unpublished', this.onUserUnpublished) // 远端用户主动取消发布
        client?.enableAudioVolumeIndicator() // 开启音量回调
        client?.on('volume-indicator', (result) => {
          // 传参设置音量
          audioStore.setVolumeIndicator(
            result.map((v) => {
              return {
                uid: Number(v.uid),
                volume: v.level
              }
            })
          )
        }) // 监听音量范围操作，可以用来做麦位光圈操作
        // 进入频道
        const userStore = useUserStore()
        const isJoin = await client?.join(
          roomStore.rtcInfo.appId,
          String(roomStore.rtcInfo.channel),
          roomStore.rtcInfo.token,
          Number(roomStore.rtcInfo.uid)
        )

        console.log(
          'AgoraSDK => 加入频道',
          isJoin ? '成功' : '失败',
          roomStore.roomId.toString(),
          userStore.userInfo.uid
        )

        // 通话房的采集设备
        if (
          roomStore.roomType === constant.ROOM_TYPE.VIDEO_CALL ||
          (roomStore.roomType === constant.ROOM_TYPE.VIDEO_LIVE &&
            roomStore.isHostLive)
        ) {
          this.manageLocalAllTrack(true)
        }

        // 语音房初始化时 如果用户自己在麦位上就发布本地音频轨道
        if (
          roomStore.roomType === constant.ROOM_TYPE.AUDIO_LIVE &&
          audioStore.isOnMicSeat
        ) {
          this.manageLocalAudioMicrophone(true)
        }

        this.joined = true
        // 如果还没此状态还没更新，就更新
        if (
          roomStore.isShowMask &&
          roomStore.roomType === constant.ROOM_TYPE.VIDEO_LIVE
        ) {
          this.setIsShowMask(roomStore.isStartLive)
        }
      } catch (error) {
        console.log('AgoraSDK => 进房函数报错', error)
      }
    },
    /**
     * 设置角色
     */
    async setRole(value: 'host' | 'audience') {
      const roomStore = useRoomStore()
      const audioStore = useAudioStore()
      // 不同值才设置
      if (this.role !== value) {
        console.log('AgoraSDK => 开始设置角色', value)
        this.role = value
        // 如果是主播
        if (this.role == 'host') {
          // 如果进房成功
          if (this.joined && audioStore.rtcInfoMin.token) {
            await client?.renewToken(audioStore.rtcInfoMin.token)
          }
          await client?.setClientRole(this.role)

          console.log('AgoraSDK => 设置主播角色成功', value)
        } else if (this.role === 'audience') {
          // 如果是其他角色
          // 如果进房成功
          if (this.joined) {
            await client?.renewToken(roomStore.rtcInfo.token)
          }
          await client?.setClientRole(this.role, { level: 1 })
          console.log('AgorSDK => 设置观众角色成功', value)
        }
      }
    },
    /** 重置所有设备 */
    resetAllTracks() {
      console.log('AgoraSDK => 重置所有设备')
      this.audioTrack && this.audioTrack.close()
      this.audioTrack = undefined
      this.videoTrack && this.videoTrack.close()
      this.videoTrack = undefined
    },
    /**
     * 启用或禁用所有轨道
     * 轨道禁用后，播放和发布都将被停止
     * @param {boolean} enabled true:启用，false：禁用
     * */
    async setEnabledAllTracks(enabled: boolean) {
      console.log('AgoraSDK => 触发启用或禁用所有轨道', enabled)
      if (this.audioTrack && this.videoTrack) {
        console.log('AgoraSDK => 开始启用或禁用所有轨道', enabled)
        await this.audioTrack.setEnabled(enabled)
        await this.videoTrack.setEnabled(enabled)
      }
    },
    /**
     * 采集设备,通话房适用
     * */
    async initAllTracks() {
      console.log('AgoraSDK => 采集设备，1v1房间、直播、开始直播情况适用')
      console.log(common.checkVersion().mobile)
      this.resetAllTracks()
      let tracks: [IMicrophoneAudioTrack, ICameraVideoTrack]
      const AgoraRTC = window.AgoraRTC
      try {
        tracks = await Promise.all([
          AgoraRTC.createMicrophoneAudioTrack(),
          AgoraRTC.createCameraVideoTrack({
            facingMode: this.facingMode,
            encoderConfig: {
              width: 480,
              height: 640,
              frameRate: 15,
              bitrateMin: 1200,
              bitrateMax: 1200
            }
          })
        ])
        this.audioTrack = tracks[0]
        this.videoTrack = tracks[1]

        // this.videoTrack.getMediaStreamTrack().getSettings().facingMode 可以用来判断摄像头方向

        // 如果被禁用就启用
        if (!this.audioTrack.enabled || !this.videoTrack.enabled) {
          await this.setEnabledAllTracks(true)
        }
      } catch (error) {
        console.log('AgoraSDK => 通话房采集视频失败，然后重新采集', error)
        if (JSON.stringify(error).indexOf('PERMISSION_DENIED') != -1) {
          // 授权“麦克风”权限
          this.setAuthPopupConfig(true)
        }
        showToast(error)
      }
    },
    /** 设置前后置摄像头 */
    async switchCamera() {
      const i18n = useNuxtApp().$i18n
      showLoadingToast()
      if (
        this.videoinputNum >= 2 &&
        this.videoTrack &&
        this.videoTrack.enabled
      ) {
        console.log('AgoraSDK => 切换前后置摄像头')
        const facingMode = this.facingMode === 'user' ? 'environment' : 'user'
        await this.videoTrack
          ?.setDevice({
            facingMode: facingMode
          })
          .then(() => {
            // 更新前后置
            showToast(i18n.t('public.Success'))
            this.facingMode = facingMode
            console.log('AgoraSDK => 摄像头朝向', this.facingMode || '无')
          })
          .catch(() => {
            showToast(i18n.t('live.failedSwitch'))
          })
      } else {
        showToast(i18n.t('live.failedSwitch'))
      }
    },
    // 当远端用户成功发布音视频轨道之后
    async onUserPublished(user, mediaType) {
      console.log('AgoraSDK => 远程用户', mediaType, user)
      const roomStore = useRoomStore()
      const audioStore = useAudioStore()

      // 视频才显示遮罩层
      if (mediaType === 'video') {
        this.setIsShowMask(true)
      }

      // 发起订阅，视频房和通话房默认订阅 语音房扬声器打开了也默认订阅
      if (
        roomStore.roomType === constant.ROOM_TYPE.VIDEO_LIVE ||
        roomStore.roomType === constant.ROOM_TYPE.VIDEO_CALL ||
        audioStore.isOpenSound
      ) {
        await client?.subscribe(user, mediaType)
      }
      delete this.remoteUsers[user.uid]
      delete agoraRemoteUsers[user.uid]
      this.remoteUsers[user.uid] = user // 设置远程用户
      agoraRemoteUsers[user.uid] = user // 设置远程用户

      // 视频轨道或语音房才操作远程用户, 监听远程用户
      if (
        mediaType === 'video' ||
        (roomStore.roomType === constant.ROOM_TYPE.AUDIO_LIVE &&
          mediaType === 'audio')
      ) {
        this.remoteUsersChange(false)
      }

      // 播放语音 如果需要自动播放就用
      if (
        roomStore.roomType === constant.ROOM_TYPE.AUDIO_LIVE &&
        mediaType === 'audio' &&
        audioStore.isOpenSound
      ) {
        user.audioTrack.play()
      }

      // 监听首帧视频解码时触发，视频轨道才会触发
      this.remoteUsers[user.uid].videoTrack?.on(
        'first-frame-decoded',
        async () => {
          console.log('AgoraSDK => 远程用户首帧视频解码时触发', user, mediaType)
          this.isRemoteUserPirstFrame = true
          this.setIsShowMask(false)
          // 发布成功，判断清除发布超时
          roomStore.closeIsTimeout()

          // 兼容安卓，让安卓移除遮挡层
          if (roomStore.roomType === constant.ROOM_TYPE.VIDEO_CALL) {
            // app不发送
            if (!roomStore.isApp) {
              const socketStore = useSocketStore()
              const req_data = {
                custom: {
                  first: 40,
                  second: 24
                },
                uid: socketStore.personRoomMemberInfo.uid,
                member: JSON.stringify(socketStore.personRoomMemberInfo),
                room_id: roomStore.roomId,
                timestamp: new Date().getTime()
              }
              socketStore.sendMessage(req_data)
            }
            this.isStartBilling()
          }
        }
      )
    },
    // 用户端主动取消发布
    onUserUnpublished(user, mediaType) {
      console.log('AgoraSDK => 离开的远程用户', mediaType, user)
      const roomStore = useRoomStore()

      // 视频直播或视频通话房 移除离开的远程用户
      if (
        (roomStore.roomType === constant.ROOM_TYPE.VIDEO_LIVE &&
          mediaType === 'video') ||
        (roomStore.roomType === constant.ROOM_TYPE.VIDEO_CALL &&
          mediaType === 'video')
      ) {
        delete this.remoteUsers[user.uid]
        delete agoraRemoteUsers[user.uid]
        this.remoteUsersChange(false) // 监听远程用户
      }
      // 语音房
      if (roomStore.roomType === constant.ROOM_TYPE.AUDIO_LIVE) {
        delete this.remoteUsers[user.uid]
        delete agoraRemoteUsers[user.uid]
        this.remoteUsersChange(false) // 监听远程用户
      }
      // 取消订阅
      // await client.unsubscribe(user, mediaType)
    },
    /**
     * 视频房使用
     * 管理本地音视频发布与取消发布,发送或暂停发布
     * @param {boolean} isPut true:发布，发送, false: 暂停
     * @param {boolean} isUnpublish 是否强制取消发布
     */
    async manageLocalAllTrack(isPut: boolean, isUnpublish?: false) {
      console.log('AgoraSDK => manageLocalAllTrack')
      const roomStore = useRoomStore()

      // 不符合条件就return
      if (
        !(
          roomStore.roomType === constant.ROOM_TYPE.VIDEO_CALL ||
          (roomStore.roomType === constant.ROOM_TYPE.VIDEO_LIVE &&
            roomStore.isHostLive)
        )
      )
        return

      // 发布，发送
      if (isPut) {
        // 如果已经进入过房间完毕就用setMuted发送
        if (this.joined && this.audioTrack && this.videoTrack) {
          await this.videoTrack.setMuted(false)
          await this.audioTrack.setMuted(false)

          // 没进入过就重新初始化发布
        } else {
          // 没进入过房间就重新获取发布
          // 有就不重新获取
          if (!(this.audioTrack && this.videoTrack)) {
            await this.initAllTracks()
          }

          // 如果被禁用就启用
          if (!this.audioTrack?.enabled || !this.videoTrack?.enabled) {
            await this.setEnabledAllTracks(true)
          }

          // 主播关闭蒙版
          if (roomStore.isHostLive) {
            this.setIsShowMask(false)
          }

          const tracks: [IMicrophoneAudioTrack, ICameraVideoTrack] = [
            this.audioTrack as IMicrophoneAudioTrack,
            this.videoTrack as ICameraVideoTrack
          ]
          await client?.publish(tracks).then(() => {
            console.log('AgoraSDK => 发布成功')
            this.isVideoPublish = true
            this.isStartBilling()

            // 发布成功，判断清除发布超时
            roomStore.closeIsTimeout()
          })
        }
      } else {
        // 取消发布或暂停一定要要joined：true 如果可以就用setMuted暂停
        if (this.joined && this.audioTrack && this.videoTrack) {
          if (isUnpublish) {
            const tracks: [IMicrophoneAudioTrack, ICameraVideoTrack] = [
              this.audioTrack as IMicrophoneAudioTrack,
              this.videoTrack as ICameraVideoTrack
            ]
            await client?.unpublish(tracks).then(() => {
              // 取消就显示遮罩层
              this.setIsShowMask(true)
              console.log('AgoraSDK => 取消发布成功')
            })
          } else {
            await this.videoTrack.setMuted(true)
            await this.audioTrack.setMuted(true)
          }
        }
      }
    },
    /**
     * 语音房使用
     * 管理本地上下麦克风
     * @param {boolean} isPut true:上麦, false:下麦
     */
    async manageLocalAudioMicrophone(isPut: boolean) {
      console.log('AgoraSDK => 语音房管理麦克风', isPut)
      const roomStore = useRoomStore()
      const AgoraRTC = window.AgoraRTC
      // 有本地音频轨道就发布
      if (this.audioTrack) {
        if (isPut) {
          if (roomStore.roomType === constant.ROOM_TYPE.AUDIO_LIVE) {
            await this.setRole('host')
          }
          await this.audioTrack.setEnabled(true)
          client?.publish(this.audioTrack as IMicrophoneAudioTrack) // 发布
          // this.audioTrack.play()
        } else {
          await client?.unpublish(this.audioTrack as IMicrophoneAudioTrack) // 取消发布
          await this.audioTrack.setEnabled(false)
          // 语音房才做身份切换
          if (roomStore.roomType === constant.ROOM_TYPE.AUDIO_LIVE) {
            this.setRole('audience')
          }
        }
      } else {
        // 如果没有本地音频
        AgoraRTC.createMicrophoneAudioTrack()
          .then(async (audioTrack: IMicrophoneAudioTrack) => {
            this.audioTrack = audioTrack
            if (isPut) {
              await this.setRole('host')
              client?.publish(this.audioTrack as IMicrophoneAudioTrack) // 发布
              // this.audioTrack.play()
            } else {
              await client?.unpublish(this.audioTrack as IMicrophoneAudioTrack) // 取消发布
              this.setRole('audience')
            }
          })
          .catch((err) => {
            if (JSON.stringify(err).indexOf('PERMISSION_DENIED') != -1) {
              // 授权“麦克风”权限
              this.setAuthPopupConfig(true)
              const audioStore = useAudioStore()
              // 麦克风图标显示
              audioStore.setIsOpenMic(false)
            }
          })
      }
    },
    /**
     * 语音房使用
     * 开关远程所有音频轨道
     * @param {boolean} isPlay true:播放, false:不播放
     */
    switchAllRemoteAudioTrack(isPlay: boolean) {
      console.log('AgoraSDK => 语音房是否播放音频', isPlay)
      if (isPlay) {
        // 播放全部远端音频
        Object.keys(agoraRemoteUsers).forEach(async (v) => {
          if (agoraRemoteUsers[v]) {
            await client?.subscribe(agoraRemoteUsers[v], 'audio')
            agoraRemoteUsers[v].audioTrack?.play()
          }
        })
      } else {
        // 取消全部远端音频
        Object.keys(agoraRemoteUsers).forEach(async (v) => {
          if (agoraRemoteUsers[v]) {
            await client?.unsubscribe(agoraRemoteUsers[v], 'audio')
          }
        })
      }
    },
    // room遮罩层设置,视频的房间才可以操作遮罩层
    setIsShowMask(show: boolean) {
      const roomStore = useRoomStore()
      if (
        roomStore.roomType === constant.ROOM_TYPE.VIDEO_LIVE ||
        roomStore.roomType === constant.ROOM_TYPE.VIDEO_CALL
      ) {
        roomStore.setIsShowMask(show)
      }
    },
    /**
     * 监听远程用户
     * @param {boolean} isInit 是否初始化
     */
    remoteUsersChange(init: boolean) {
      const roomStore = useRoomStore()
      roomStore.whetherHostIsLive(init ? [] : Object.keys(this.remoteUsers)) // 监听远程用户
    },
    /**
     * 显示授权弹窗
     */
    setAuthPopupConfig(value: boolean) {
      this.authPopupConfig.isLazyShow = value
      this.authPopupConfig.isShow = value
    },
    // 离开
    async leave() {
      // 监听远程用户
      this.remoteUsersChange(true)
      await client?.leave()
      this.resetData()
      window.AgoraRTC.enableLogUpload()
      console.log('AgoraSDK => 离开成功')
    },
    // 重置数据
    resetData() {
      this.resetAllTracks()
      this.remoteUsers = {}
      agoraRemoteUsers = {}
      this.isVideoPublish = false
      this.isRemoteUserPirstFrame = false
      this.joined = false
    }
  }
})
